"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Scalar API reference mounted the supported way: one CDN script (exact
 * version pinned — a silent 1.x bump changed the layout once already) and
 * `Scalar.createApiReference(element, config)` from an effect, so the
 * reference survives client-side navigation (a `next/script` tag only ran on
 * the first visit) and is torn down on unmount. Renders a placeholder until
 * Scalar has taken over the container so nothing jumps while the script loads.
 */

const SCALAR_SCRIPT = "https://cdn.jsdelivr.net/npm/@scalar/api-reference@1.72.1";

type ScalarApp = { destroy?: () => void };
type ScalarGlobal = { createApiReference: (el: HTMLElement, config: Record<string, unknown>) => ScalarApp };

let scriptPromise: Promise<ScalarGlobal> | null = null;

function loadScalar(): Promise<ScalarGlobal> {
  const existing = (window as unknown as { Scalar?: ScalarGlobal }).Scalar;
  if (existing?.createApiReference) return Promise.resolve(existing);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCALAR_SCRIPT;
    s.async = true;
    s.onload = () => {
      const g = (window as unknown as { Scalar?: ScalarGlobal }).Scalar;
      if (g?.createApiReference) resolve(g);
      else reject(new Error("Scalar loaded without createApiReference"));
    };
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("Scalar script failed to load"));
    };
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function ApiReference({ specUrl, fontFamily }: { specUrl: string; fontFamily: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");

  useEffect(() => {
    let app: ScalarApp | null = null;
    let cancelled = false;
    const el = containerRef.current;
    if (!el) return;
    loadScalar()
      .then((Scalar) => {
        if (cancelled) return;
        app = Scalar.createApiReference(el, {
          url: specUrl,
          layout: "modern",
          theme: "default",
          darkMode: true,
          forceDarkModeState: "dark",
          hideDarkModeToggle: true,
          // No browser test requests: the API has no CORS for third-party
          // origins and we don't route customer keys through Scalar's proxy.
          hideTestRequestButton: true,
          hideClientButton: true,
          hideModels: false,
          defaultOpenAllTags: false,
          persistAuth: false,
          documentDownloadType: "json",
          // Site fonts instead of a second font download from Scalar's CDN.
          withDefaultFonts: false,
          // The sticky docs header (layout.tsx, --docs-header-h) sits above
          // Scalar: keep the sidebar below it instead of sliding underneath.
          customCss: `
            .scalar-app { --scalar-font: ${fontFamily}; --scalar-font-code: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
            .scalar-app .references-layout { background: #000; }
            @media (min-width: 1000px) {
              .scalar-app aside {
                top: var(--docs-header-h, 56px) !important;
                height: calc(100dvh - var(--docs-header-h, 56px)) !important;
                max-height: calc(100dvh - var(--docs-header-h, 56px)) !important;
              }
            }
          `,
          // Scalar's hosted extras (AI chat, MCP generator, analytics) stay off:
          // this is a reference for customers' own keys, nothing leaves the page.
          agent: { disabled: true },
          mcp: { disabled: true },
          telemetry: false,
          metaData: { title: "Coldfire CRM API Reference" },
        });
        setState("ready");
      })
      .catch((e) => {
        console.error("[api-docs]", e);
        if (!cancelled) setState("error");
      });
    return () => {
      cancelled = true;
      try {
        app?.destroy?.();
      } catch {
        /* Scalar already gone */
      }
    };
  }, [specUrl, fontFamily]);

  return (
    <div className="relative min-h-[60vh]">
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-start justify-center px-6 pt-20" aria-live="polite">
          {state === "loading" ? (
            <p className="text-sm text-neutral-500">Loading the API reference…</p>
          ) : (
            <div className="max-w-md text-center text-sm text-neutral-400">
              <p className="font-medium text-white">The reference could not be loaded.</p>
              <p className="mt-2">
                The OpenAPI document is available directly:{" "}
                <a href={specUrl} className="underline underline-offset-4 hover:text-white">
                  openapi.json
                </a>
              </p>
            </div>
          )}
        </div>
      )}
      <div ref={containerRef} className={state === "ready" ? undefined : "invisible"} />
    </div>
  );
}
